import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { resolveClinicForUser } from '../lib/clinicResolver';
import Sidebar from './Sidebar';
import LoadingSpinner from './LoadingSpinner';

const PetMedicalHistoryPage = () => {
  const navigate = useNavigate();
  const { petId } = useParams();
  const [user, setUser] = useState(null);
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pet, setPet] = useState(null);
  const [medicalRecords, setMedicalRecords] = useState([]);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const toastTimeoutRef = useRef(null);

  useEffect(() => {
    checkUser();
  }, []);

  useEffect(() => {
    if (clinic && petId) {
      // Clear previous data to prevent stale data on pet change
      setMedicalRecords([]);
      setPet(null);
      fetchPet();
      fetchMedicalRecords();
    }
  }, [clinic, petId]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Cleanup toast timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  const showToast = (message, type = 'success') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ show: true, message, type });
    toastTimeoutRef.current = setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 4000);
  };

  const checkUser = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        console.error('❌ Error getting user:', userError);
        navigate('/login');
        return;
      }
      
      if (!user) {
        if (import.meta.env.DEV) console.log('❌ No user found, redirecting to login');
        navigate('/login');
        return;
      }

      setUser(user);

      // Fetch clinic data (supports both owners and vet employees)
      const { clinic: clinicData } = await resolveClinicForUser(supabase, user.id);
      if (clinicData) setClinic(clinicData);
    } catch (error) {
      console.error('❌ Unexpected error in checkUser:', error);
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const fetchPet = async () => {
    try {
      const { data, error } = await supabase
        .from('pets')
        .select(`
          *,
          owners:owner_id(
            id,
            first_name,
            last_name
          )
        `)
        .eq('id', petId)
        .single();

      if (error) throw error;
      setPet(data);
    } catch (error) {
      console.error('Error fetching pet:', error);
      showToast('Σφάλμα κατά τη φόρτωση των πληροφοριών του κατοικιδίου.', 'error');
    }
  };

  const fetchMedicalRecords = async () => {
    try {
      const { data, error } = await supabase
        .from('medical_records')
        .select('*')
        .eq('pet_id', petId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const records = data || [];

      // Fetch vets separately (no FK constraint on vet_id)
      const vetIds = [...new Set(records.map(r => r.vet_id).filter(Boolean))];
      let vetsMap = {};
      if (vetIds.length > 0) {
        const { data: vetsData } = await supabase
          .from('vets')
          .select('id, first_name, last_name')
          .in('id', vetIds);
        if (vetsData) {
          vetsMap = Object.fromEntries(vetsData.map(v => [v.id, v]));
        }
      }

      setMedicalRecords(records.map(r => ({ ...r, vets: r.vet_id ? vetsMap[r.vet_id] || null : null })));
    } catch (error) {
      console.error('Error fetching medical records:', error);
      showToast('Σφάλμα κατά τη φόρτωση των ιατρικών εγγραφών.', 'error');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('el-GR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const getTitleDisplay = (record) => {
    if (record.title === 'check up') return 'Γενικός έλεγχος';
    if (record.title === 'vaccination') return 'Εμβολιασμός';
    if (record.title === 'surgery') return 'Χειρουργείο';
    if (record.title === 'emergency') return 'Επείγον';
    return record.title || 'Άγνωστο';
  };

  const filteredRecords = useMemo(() => {
    const q = debouncedSearchTerm.trim().toLowerCase();
    if (!q) return medicalRecords;
    return medicalRecords.filter((r) => {
      const raw = (r.title || '').toLowerCase();
      const localized = getTitleDisplay(r).toLowerCase();
      const notes = (r.notes || '').toLowerCase();
      const medication = (r.medication || '').toLowerCase();
      return raw.includes(q) || localized.includes(q) || notes.includes(q) || medication.includes(q);
    });
  }, [medicalRecords, debouncedSearchTerm]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!pet) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#1A202C' }}>
        <div className="flex items-center justify-center h-screen">
          <div className="text-white text-lg">Το κατοικίδιο δεν βρέθηκε</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white flex flex-col lg:flex-row" style={{ backgroundColor: '#1A202C', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, background: 'radial-gradient(800px 800px at 10% -10%, rgba(16,185,129,0.12), transparent), radial-gradient(800px 800px at 110% 10%, rgba(59,130,246,0.08), transparent)' }} className="pointer-events-none" />

      {/* Shared Sidebar */}
      <Sidebar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />

      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 p-4 lg:p-8">
        {/* Toast Notification */}
        {toast.show && (
          <div className="fixed top-4 right-4 left-4 sm:left-auto z-50">
            <div
              className="flex items-center px-5 py-4 rounded-xl border max-w-sm ml-auto transition-all duration-300"
              style={{
                background: 'linear-gradient(135deg, rgba(17,24,39,0.95) 0%, rgba(31,41,55,0.95) 100%)',
                backdropFilter: 'blur(10px)',
                borderColor: toast.type === 'success' ? '#10B981' : toast.type === 'error' ? '#EF4444' : '#3B82F6',
                boxShadow: `0 10px 25px rgba(0,0,0,0.3), 0 0 20px ${
                  toast.type === 'success' ? 'rgba(16,185,129,0.2)' :
                  toast.type === 'error' ? 'rgba(239,68,68,0.2)' : 'rgba(59,130,246,0.2)'
                }`
              }}
            >
              <div className="flex-shrink-0 mr-3">
                {toast.type === 'success' && (
                  <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                )}
                {toast.type === 'error' && (
                  <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                )}
                {toast.type === 'info' && (
                  <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                )}
              </div>
              <div className="text-white font-medium flex-1">{toast.message}</div>
              <button
                onClick={() => setToast({ show: false, message: '', type: 'success' })}
                className="ml-4 text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-6 lg:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <button
              onClick={() => navigate('/clients')}
              className="flex items-center px-4 py-2 rounded-lg font-medium text-white transition-colors w-fit"
              style={{ backgroundColor: '#4A5568' }}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
              </svg>
              Πίσω στους Πελάτες
            </button>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => navigate(`/pet-reminders/${pet.id}`)}
                className="px-5 py-2.5 rounded-lg font-medium text-white transition-transform duration-200 hover:scale-[1.02]"
                style={{ backgroundColor: '#3B82F6' }}
              >
                Υπενθυμίσεις
              </button>
              <button
                onClick={() => navigate(`/new-medical-record/${pet.id}`)}
                className="px-5 py-2.5 rounded-lg font-medium text-white transition-transform duration-200 hover:scale-[1.02]"
                style={{ background: 'linear-gradient(90deg, #10B981, #059669)' }}
              >
                + Νέα Εγγραφή
              </button>
            </div>
          </div>
          <div className="rounded-xl overflow-hidden border p-4 lg:p-6" style={{ background: 'linear-gradient(135deg, #111827 0%, #1F2937 100%)', borderColor: '#374151', boxShadow: '0 10px 25px rgba(0,0,0,0.3)' }}>
            <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2">
              Ιατρικό Ιστορικό - {pet.name}
            </h1>
            <div className="text-gray-400 text-base lg:text-lg mb-2">
              {pet.species} • {pet.owners?.first_name} {pet.owners?.last_name}
            </div>
            {pet.breed && (
              <div className="text-gray-400 text-sm">
                Φυλή: {pet.breed}
              </div>
            )}
          </div>
        </div>

        {/* Search Bar and Record Count */}
        <div className="mb-6 lg:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Αναζήτηση με τίτλο (π.χ. Χειρουργείο), σημειώσεις ή φάρμακα..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 pl-12 rounded-lg border text-white"
                style={{ backgroundColor: '#111827', borderColor: '#374151' }}
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                </svg>
              </div>
            </div>
            <div
              className="flex items-center gap-2 px-4 py-2 rounded-xl whitespace-nowrap"
              style={{
                background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(6,182,212,0.15) 100%)',
                border: '1px solid rgba(16,185,129,0.3)'
              }}
            >
              <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
              <span className="text-emerald-400 font-medium text-sm">
                {filteredRecords.length} {filteredRecords.length === 1 ? 'εγγραφή' : 'εγγραφές'}
              </span>
            </div>
          </div>
        </div>

        {/* Medical Records List */}
        <div className="space-y-4 sm:space-y-5 lg:space-y-6">
          {filteredRecords.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <svg className="mx-auto h-16 w-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-3-3v6m9 3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <div className="text-lg mb-2">Δεν βρέθηκαν εγγραφές</div>
              <div className="text-sm">Δοκιμάστε διαφορετικό τίτλο ή όρο αναζήτησης</div>
            </div>
          ) : (
            filteredRecords.map((record) => (
              <div key={record.id} className="rounded-xl overflow-hidden border transition-all duration-300 hover:border-emerald-500/30" style={{ backgroundColor: '#111827', borderColor: '#374151', boxShadow: '0 10px 25px rgba(0,0,0,0.3)' }}>
                <div className="px-4 lg:px-6 py-4 border-b" style={{ borderColor: '#374151', background: 'linear-gradient(90deg, rgba(55,65,81,0.65), rgba(31,41,55,0.65))' }}>
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-white">{getTitleDisplay(record)}</h3>
                      <div className="text-sm text-gray-400 mt-1">
                        {record.vets ? `Δρ. ${record.vets.first_name} ${record.vets.last_name}` : 'Άγνωστος κτηνίατρος'}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-400">{formatDate(record.created_at)}</div>
                      {record.updated_at && (
                        <div className="text-xs text-gray-500">Ενημερώθηκε: {formatDate(record.updated_at)}</div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="p-4 lg:p-6">
                  {record.notes && (
                    <div className="text-sm text-gray-300 mb-4">
                      {record.notes}
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                    {record.weight && (
                      <div>
                        <span className="text-gray-400">Βάρος:</span> {record.weight} kg
                      </div>
                    )}
                    {record.temperature && (
                      <div>
                        <span className="text-gray-400">Θερμοκρασία:</span> {record.temperature}°C
                      </div>
                    )}
                    {record.medication && (
                      <div>
                        <span className="text-gray-400">Φάρμακα:</span> {record.medication}
                      </div>
                    )}
                  </div>

                  {Array.isArray(record.attachments) && record.attachments.length > 0 && (
                    <div className="mt-5">
                      <div className="text-sm text-gray-400 mb-2">Συννημμένα:</div>
                      <div className="flex flex-wrap gap-3">
                        {record.attachments.map((att, idx) => {
                          const url = typeof att === 'string' ? att : att?.url;
                          const title = typeof att === 'object' && att?.title ? att.title : null;
                          const lower = (url || '').toLowerCase();
                          const isImage = lower.match(/\.(png|jpe?g|gif|webp|svg)(\?.*)?$/);
                          const isPdf = lower.endsWith('.pdf') || lower.includes('application/pdf');
                          const label = title || (isImage ? 'Εικόνα' : isPdf ? 'PDF' : 'Σύνδεσμος');
                          return (
                            <a key={idx} href={url} target="_blank" rel="noopener noreferrer" className="group">
                              {isImage ? (
                                <img src={url} alt={label} className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-lg border" style={{ borderColor: '#374151' }} />
                              ) : (
                                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg border flex items-center justify-center text-xs text-gray-300 text-center px-2" style={{ borderColor: '#374151', backgroundColor: '#1F2937' }}>
                                  {label}
                                </div>
                              )}
                              <div className="text-[11px] text-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity mt-1 max-w-[8rem] truncate text-center">
                                {label}
                              </div>
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="mt-6 flex justify-end">
                    <button
                      onClick={() => navigate(`/medical-record/${record.id}/edit`)}
                      className="px-4 py-2 rounded-lg font-medium text-white transition-transform duration-200 hover:scale-[1.02]"
                      style={{ backgroundColor: '#3B82F6' }}
                    >
                      ✏️ Επεξεργασία
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default PetMedicalHistoryPage;
